import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Mail, Loader2, Plus, Trash2, Users, Download, X, Send } from 'lucide-react';
import { useState } from 'react';
interface Subscriber {
  id: string;
  email: string;
  name: string;
  status: string;
  subscribed_at: string;
}

type Campaign = {
  id: string;
  subject: string;
  recipient_count: number;
  sent_at: string | null;
};

export default function MailingListPage() {
  const queryClient = useQueryClient();

  const { data: subscribers, isLoading } = useQuery<Subscriber[]>({
    queryKey: ['mailing-list'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/mailing-list/');
      return res.data;
    },
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: ['newsletter-log'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/mailing-list/campaigns/');
      return res.data;
    },
  });

  // Add subscriber
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  const addMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      // Get own profile first
      const profRes = await api.get('/advisor/profile/mine/');
      const profile = profRes.data;
      return api.post('/advisor/mailing-list/', {
        profile: profile.id,
        email: newEmail,
        name: newName,
        status: 'active',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailing-list'] });
      setNewEmail('');
      setNewName('');
      setShowAdd(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/advisor/mailing-list/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailing-list'] });
    },
  });

  // CSV Export
  const exportCsv = () => {
    if (!subscribers) return;
    const header = 'Email,Name,Status,Subscribed Date\n';
    const rows = subscribers.map(s =>
      `"${s.email}","${s.name}","${s.status}","${new Date(s.subscribed_at).toLocaleDateString()}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mailing-list.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  const activeCount = (subscribers || []).filter(s => s.status === 'active').length;
  const totalCount = (subscribers || []).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Mailing List</h1>
          <p className="text-muted-foreground">The people who asked to hear from you, and what they have been sent</p>
        </div>
        <div className="flex items-center gap-2">
          {totalCount > 0 && (
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          )}
          <Button onClick={() => setShowAdd(true)} data-testid="add-subscriber-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Subscriber
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3" data-testid="mailing-stats">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-amber-600">{activeCount}</div>
            <div className="text-xs text-muted-foreground">Active Subscribers</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold">{totalCount}</div>
            <div className="text-xs text-muted-foreground">Total Subscribers</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-green-600">
              {activeCount > 0 ? Math.round((activeCount / totalCount) * 100) : 0}%
            </div>
            <div className="text-xs text-muted-foreground">Active Rate</div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="newsletter-log">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Send className="h-4 w-4 text-amber-500" /> Sent to subscribers
          </CardTitle>
          <CardDescription>
            There is no newsletter to compose. Write a blog post, tick
            &ldquo;send this to my mailing list&rdquo;, and publishing it sends
            it to everybody here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(campaigns || []).length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground" data-testid="newsletter-log-empty">
              Nothing sent yet.
            </p>
          ) : (
            <div className="space-y-2">
              {(campaigns || []).map((campaign: Campaign) => (
                <div
                  key={campaign.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                  data-testid={`newsletter-log-${campaign.id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{campaign.subject}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {campaign.sent_at
                        ? new Date(campaign.sent_at).toLocaleString()
                        : 'Not sent'}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {campaign.recipient_count} recipient
                    {campaign.recipient_count === 1 ? '' : 's'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add subscriber form */}
      {showAdd && (
        <Card className="border-amber-500/30" data-testid="add-subscriber-form">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4 text-amber-500" /> Add Subscriber
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowAdd(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@example.com"
                type="email"
                data-testid="subscriber-email-input"
              />
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name (optional)"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => addMutation.mutate()}
                  disabled={!newEmail.trim() || addMutation.isPending}
                  data-testid="submit-subscriber-btn"
                >
                  {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                </Button>
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscriber list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-500" /> Subscribers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {totalCount > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subscribers-table">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs">
                    <th className="text-left py-2 pr-3 font-medium">Email</th>
                    <th className="text-left py-2 pr-3 font-medium">Name</th>
                    <th className="text-left py-2 pr-3 font-medium">Status</th>
                    <th className="text-left py-2 pr-3 font-medium">Subscribed</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(subscribers || []).map(sub => (
                    <tr key={sub.id} className="border-b border-muted/50 hover:bg-muted/30" data-testid={`subscriber-${sub.id}`}>
                      <td className="py-2 pr-3 font-medium">{sub.email}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{sub.name || '—'}</td>
                      <td className="py-2 pr-3">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${sub.status === 'active' ? 'border-green-500/30 text-green-600' : 'border-muted text-muted-foreground'}`}
                        >
                          {sub.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground text-xs">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </td>
                      <td className="py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => deleteMutation.mutate(sub.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12" data-testid="no-subscribers">
              <Mail className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground font-medium">No subscribers yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add subscribers manually or enable the subscription widget on your public profile.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
